import React, { memo } from "react";
import styles from "./radio-button.module.scss";
import classnames from "classnames";
import type { BaseRadioButtonProps } from "@whatslly/types/UI-V2/BaseElements/RadioButtonType";
import globalStyles from "../../globalStyles";

export const RadioButton: React.FC<BaseRadioButtonProps> = memo(
  ({
    paddingInline,
    borderColor,
    marginTop,
    marginLeft,
    marginRight,
    textFontSize,
    backgroundColor,
    textFontWeight,
    marginBottom,
    className,
    name,
    selectedOptionColor,
    padding,
    options = [],
    defaultCheck,
    radioButtonSize,
    textClassName,
    radioButtonContainerClassName,
    hoverColor,
    textColor,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    onChange,
    ...props
  }) => {
    const handleHover = (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
      const target = e.target as HTMLLabelElement;
      if (target.localName === "span") {
        target.classList.remove(globalStyles[`bg-color-${backgroundColor ?? "main-secondary"}`]);
        target.classList.add(globalStyles[`bg-color-${hoverColor ?? "neutral-secondary"}`]);
      }
    };

    const handleBlur = (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
      const target = e.target as HTMLLabelElement;
      if (target.localName === "span") {
        target.classList.remove(globalStyles[`bg-color-${hoverColor ?? "neutral-secondary"}`]);
        target.classList.add(globalStyles[`bg-color-${backgroundColor ?? "main-secondary"}`]);
      }
    };

    return (
      <div
        className={classnames(
          styles.checkBoxContainer,
          globalStyles[`p-${padding ?? "none"}`],
          globalStyles[`pi-${paddingInline ?? ""}`],
          globalStyles[`pt-${paddingTop ?? ""}`],
          globalStyles[`pb-${paddingBottom ?? ""}`],
          globalStyles[`pl-${paddingLeft ?? ""}`],
          globalStyles[`pr-${paddingRight ?? ""}`],
          globalStyles[`mb-${marginBottom ?? ""}`],
          globalStyles[`ml-${marginLeft ?? ""}`],
          globalStyles[`mr-${marginRight ?? ""}`],
          globalStyles[`mt-${marginTop ?? ""}`],
          radioButtonContainerClassName
        )}
      >
        {options.map((option, key) => (
          <div
            key={key}
            className={classnames(
              styles.radioContainer,
              globalStyles[
                `mt-${
                  !radioButtonSize
                    ? "small"
                    : ["small", "default"].includes(radioButtonSize)
                      ? "small"
                      : ["large", "extra-large"].includes(radioButtonSize)
                        ? "default"
                        : radioButtonSize === "extra-small"
                          ? "extra-small"
                          : "small"
                }`
              ]
            )}
          >
            <label
              className={classnames(styles.radioLabel, globalStyles[`radio-button-${key}`], globalStyles[`mr-extra-small`], className)}
              data-testid={`radio-button-${typeof option === "string" ? option : option.value}`}
            >
              <input
                type="radio"
                defaultChecked={typeof option === "string" ? defaultCheck === option : defaultCheck === option.value}
                name={name ?? "radio"}
                value={typeof option === "string" ? option : option.value}
                onChange={onChange}
              />
              <span
                onMouseEnter={handleHover}
                onMouseLeave={handleBlur}
                className={classnames(
                  styles.checkmark,
                  globalStyles[`bg-color-${backgroundColor ?? "main-secondary"}`],
                  globalStyles[`bc-${borderColor ?? "main-primary"}`]
                )}
                data-testid={props["data-testid"] ? `${props["data-testid"]}-${key}` : undefined}
              >
                <span className={classnames(styles.checkedElement, globalStyles[`bg-color-${selectedOptionColor ?? "main-primary"}`])} />
              </span>
            </label>
            <span
              className={classnames(
                globalStyles[`fs-${textFontSize ?? "default"}`],
                globalStyles[`fw-${textFontWeight ?? "default"}`],
                globalStyles[`color-${textColor ?? "main-primary"}`],
                textClassName
              )}
            >
              {typeof option === "string" ? option : option.optionLabel}
            </span>
          </div>
        ))}
      </div>
    );
  }
);
